import { useEffect, useState, useCallback } from "react";
import { LogOut, Truck, MapPin, Phone, Printer, ChevronRight, PackageCheck, History, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useUnreadMessages from "@/hooks/useUnreadMessages";
import { Loader, EmptyState, StatusBadge, ImageUploader } from "@/components/common";
import { formatFCFA } from "@/lib/format";
import { DELIVERY_STATUS } from "@/lib/labels";
import { toast } from "sonner";

const DONE = ["livree", "echouee", "cloturee"];

export default function LivreurApp() {
  const { user, logout } = useAuth();
  const msgUnread = useUnreadMessages();
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState(null);
  const [tab, setTab] = useState("active");
  const [proofFor, setProofFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(() => { api.get("/deliveries").then((r) => setDeliveries(r.data)).catch(() => setDeliveries([])); }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (did, status) => {
    try { await api.put(`/deliveries/${did}/status`, null, { params: { status } }); toast.success("Statut mis à jour"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const list = (deliveries || []).filter((d) => tab === "active" ? !DONE.includes(d.status) : DONE.includes(d.status));

  return (
    <div className="min-h-screen bg-[#111827] text-slate-100 pb-8">
      <header className="sticky top-0 z-40 bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center"><Truck className="w-5 h-5 text-white" /></div>
            <div><p className="font-display font-extrabold leading-none">Espace Livreur</p><p className="text-[11px] text-slate-400">{user?.name}</p></div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate("/messages")} className="text-slate-400 p-2 relative" data-testid="livreur-messages"><MessageSquare className="w-5 h-5" />{msgUnread > 0 && <span className="absolute top-0 right-0 bg-[#DC2626] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{msgUnread}</span>}</button>
            <button onClick={() => { logout(); navigate("/login"); }} className="text-slate-400 p-2" data-testid="livreur-logout"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 flex gap-2 pb-2">
          <TabBtn active={tab === "active"} onClick={() => setTab("active")} icon={PackageCheck} testid="livreur-tab-active">À livrer</TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} icon={History} testid="livreur-tab-history">Historique</TabBtn>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5">
        {!deliveries ? <Loader /> : list.length === 0 ? (
          <EmptyState icon={Truck} title={tab === "active" ? "Aucune livraison en cours" : "Aucun historique"} subtitle="Les livraisons de vos tournées apparaîtront ici." />
        ) : (
          <div className="space-y-3">
            {list.map((d) => (
              <div key={d.id} className="bg-[#1F2937] border border-slate-700 rounded-xl p-4" data-testid={`delivery-${d.id}`}>
                <div className="flex items-center gap-2 flex-wrap"><span className="font-num font-bold">{d.order_number}</span><StatusBadge map={DELIVERY_STATUS} value={d.status} /></div>
                <p className="text-sm text-slate-300 mt-1">{d.customer_name}</p>
                <p className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {d.zone} · {d.address?.address || ""}</p>
                {(d.vehicle || d.vehicle_plate) && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Truck className="w-3.5 h-3.5" /> {[d.vehicle, d.vehicle_plate].filter(Boolean).join(" · ")}</p>}

                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => setDetailFor(d.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 border border-slate-600 rounded-lg px-3 py-2" data-testid={`deliver-detail-${d.id}`}>Détail <ChevronRight className="w-3.5 h-3.5" /></button>
                  <button onClick={() => window.open(`/bordereau/${d.id}`, "_blank")} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 border border-slate-600 rounded-lg px-3 py-2" data-testid={`deliver-bordereau-${d.id}`}><Printer className="w-3.5 h-3.5" /> Bordereau</button>
                </div>

                {!DONE.includes(d.status) && (
                  <div className="flex gap-2 mt-2">
                    {d.status === "affectee" && <button onClick={() => setStatus(d.id, "en_preparation")} className="flex-1 bg-slate-700 text-white text-sm font-bold py-2 rounded-lg" data-testid={`deliver-prepare-${d.id}`}>Préparer</button>}
                    {d.status === "en_preparation" && <button onClick={() => setStatus(d.id, "en_cours")} className="flex-1 bg-amber-600 text-white text-sm font-bold py-2 rounded-lg" data-testid={`deliver-start-${d.id}`}>Démarrer la livraison</button>}
                    {d.status === "en_cours" && <button onClick={() => setProofFor(d)} className="flex-1 bg-[#16A34A] text-white text-sm font-bold py-2 rounded-lg" data-testid={`deliver-proof-${d.id}`}>Preuve de livraison</button>}
                    <button onClick={() => setStatus(d.id, "echouee")} className="bg-[#7F1D1D] text-white text-sm font-bold py-2 px-3 rounded-lg" data-testid={`deliver-fail-${d.id}`}>Échec</button>
                  </div>
                )}
                {d.proof && <p className="text-xs text-green-400 mt-2">Reçu par {d.proof.receiver_name}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
      {proofFor && <ProofModal delivery={proofFor} onClose={() => setProofFor(null)} onDone={load} />}
      {detailFor && <DetailModal id={detailFor} onClose={() => setDetailFor(null)} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition ${active ? "bg-[#2563EB] text-white" : "bg-slate-800 text-slate-400"}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

function DetailModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/deliveries/${id}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [id]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#1F2937] text-slate-100 rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-5 border border-slate-700 max-h-[90vh] overflow-y-auto" data-testid="livreur-detail-modal">
        {!data ? <Loader /> : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2"><span className="font-num font-bold">{data.delivery.order_number}</span><StatusBadge map={DELIVERY_STATUS} value={data.delivery.status} /></div>
            <div>
              <p className="text-xs uppercase text-slate-400">Client</p>
              <p className="font-semibold">{data.delivery.customer_name}</p>
              {data.order?.customer_phone && <a href={`tel:${data.order.customer_phone}`} className="text-blue-400 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {data.order.customer_phone}</a>}
              <p className="text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {data.delivery.zone} · {data.delivery.address?.address}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-400 mb-1">Articles à livrer</p>
              {(data.order?.items || []).map((i, idx) => (
                <div key={idx} className="flex justify-between"><span className="text-slate-300">{i.quantity}× {i.name}</span><span className="font-num text-slate-400">{formatFCFA(i.price * i.quantity)}</span></div>
              ))}
              <div className="flex justify-between font-bold border-t border-slate-700 pt-1 mt-1"><span>Total</span><span className="font-num">{formatFCFA(data.order?.total || 0)}</span></div>
            </div>
            <button onClick={() => window.open(`/bordereau/${id}`, "_blank")} className="w-full bg-[#2563EB] text-white font-bold py-2.5 rounded-lg inline-flex items-center justify-center gap-2"><Printer className="w-4 h-4" /> Voir le bordereau</button>
            <button onClick={onClose} className="w-full border border-slate-600 py-2.5 rounded-lg font-semibold">Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProofModal({ delivery, onClose, onDone }) {
  const [f, setF] = useState({ receiver_name: "", phone: "", comment: "", photo: [] });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/deliveries/${delivery.id}/proof`, { ...f, photo: f.photo[0] || "" });
      toast.success("Livraison confirmée"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#1F2937] text-slate-100 rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-5 border border-slate-700">
        <h2 className="font-display text-xl font-bold mb-4">Preuve de livraison — {delivery.order_number}</h2>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Nom du réceptionnaire" value={f.receiver_name} onChange={(e) => setF({ ...f, receiver_name: e.target.value })} className="w-full bg-[#111827] border border-slate-700 rounded-lg px-3 py-2.5 text-sm" data-testid="proof-receiver" />
          <input placeholder="Téléphone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-[#111827] border border-slate-700 rounded-lg px-3 py-2.5 text-sm" data-testid="proof-phone" />
          <textarea placeholder="Commentaire" value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} rows={2} className="w-full bg-[#111827] border border-slate-700 rounded-lg px-3 py-2.5 text-sm" data-testid="proof-comment" />
          <ImageUploader value={f.photo} onChange={(u) => setF({ ...f, photo: u })} multiple={false} testid="proof-photo" label="+ Photo de livraison" />
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-600 py-2.5 rounded-lg font-semibold">Annuler</button>
            <button className="flex-1 bg-[#16A34A] text-white py-2.5 rounded-lg font-bold" data-testid="proof-submit">Confirmer</button>
          </div>
        </form>
      </div>
    </div>
  );
}
